/**
 * People IQ — Employee self-service ("My Profile").
 * Any Asset IQ user linked via `linked_user_id` on their employee record can:
 *   - view their own profile (with sensitive-field masking)
 *   - submit change requests for allowed fields (HR must approve)
 *   - see their in-flight / historical request statuses
 */
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDate, formatDateTime, formatINR } from "@/lib/permissions";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Send, User, Clock, CheckCircle2, XCircle, PhoneCall, FileText, Eye, IndianRupee } from "lucide-react";

// Fields the employee is allowed to REQUEST changes on. Legal identity /
// lifecycle / employee_code are NEVER in this list (mirrors backend guard).
const REQUESTABLE_FIELDS = [
  { key: "preferred_name", label: "Preferred name" },
  { key: "personal_email", label: "Personal email" },
  { key: "personal_mobile", label: "Personal mobile" },
  { key: "official_mobile", label: "Official mobile" },
  { key: "marital_status", label: "Marital status" },
  { key: "blood_group", label: "Blood group" },
  { key: "date_of_birth", label: "Date of birth" },
  { key: "pan", label: "PAN" },
  { key: "aadhaar", label: "Aadhaar (12 digits)" },
  { key: "voter_id", label: "Voter ID" },
  { key: "driving_license", label: "Driving license" },
  { key: "esic_number", label: "ESIC number" },
  { key: "uan_number", label: "PF / UAN Number" },
  { key: "bank_account_number", label: "Bank account #" },
  { key: "bank_ifsc", label: "Bank IFSC" },
  { key: "bank_name", label: "Bank name" },
];

// Sep 2026 — blood group is a fixed dropdown, never free text.
const BLOOD_GROUPS = [
  { v: "O+", l: "O positive (O+)" }, { v: "O-", l: "O negative (O-)" },
  { v: "A+", l: "A positive (A+)" }, { v: "A-", l: "A negative (A-)" },
  { v: "B+", l: "B positive (B+)" }, { v: "B-", l: "B negative (B-)" },
  { v: "AB+", l: "AB positive (AB+)" }, { v: "AB-", l: "AB negative (AB-)" },
];

const STATUS_META = {  pending: { label: "Pending review", tint: "bg-amber-100 text-amber-800", icon: Clock },
  approved: { label: "Approved", tint: "bg-emerald-100 text-emerald-700", icon: CheckCircle2 },
  rejected: { label: "Rejected", tint: "bg-rose-100 text-rose-700", icon: XCircle },
  withdrawn: { label: "Withdrawn", tint: "bg-slate-100 text-slate-600", icon: XCircle },
};

export default function MyProfile() {
  const { user } = useAuth();
  const [emp, setEmp] = useState(null);
  const [requests, setRequests] = useState([]);
  const [notLinked, setNotLinked] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [changes, setChanges] = useState({});
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  // Sep 2026 — emergency contact is updated directly (no approval queue)
  const [ecOpen, setEcOpen] = useState(false);
  const [ecForm, setEcForm] = useState({ name: "", relation: "", phone: "" });
  const [ecSaving, setEcSaving] = useState(false);
  // Sep 2026 — view-only "My Documents" with in-window preview
  const [docs, setDocs] = useState([]);
  const [preview, setPreview] = useState(null);
  const [salary, setSalary] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/people/me/employee");
      setEmp(data);
      const { data: rs } = await api.get("/people/me/change-requests");
      setRequests(rs);
      const { data: ds } = await api.get("/people/me/documents");
      setDocs(ds);
      try {
        const { data: sal } = await api.get("/people/me/salary");
        setSalary(sal);
      } catch { setSalary(null); }
    } catch (e) {
      if (e.response?.status === 404) setNotLinked(true);
      else toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
    }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    const chObj = {};
    Object.entries(changes).forEach(([k, v]) => { if (v !== "" && v !== undefined) chObj[k] = v; });
    if (Object.keys(chObj).length === 0) return toast.error("Change at least one field before submitting");
    setSaving(true);
    try {
      await api.post("/people/me/employee/change-requests", { changes: chObj, reason: reason || null });
      toast.success("Change request submitted — HR will review it shortly");
      setDialogOpen(false); setChanges({}); setReason("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    finally { setSaving(false); }
  };

  const withdraw = async (r) => {
    if (!window.confirm("Withdraw this pending request?")) return;
    try {
      await api.post(`/people/change-requests/${r.id}/withdraw`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  const openEc = () => {
    setEcForm({
      name: emp.emergency_contact_name || "",
      relation: emp.emergency_contact_relation || "",
      phone: emp.emergency_contact_phone || "",
    });
    setEcOpen(true);
  };

  const saveEc = async () => {
    if (!ecForm.name.trim() || !ecForm.relation.trim() || !ecForm.phone.trim()) {
      return toast.error("Name, relationship and phone are all required");
    }
    setEcSaving(true);
    try {
      await api.put("/people/me/emergency-contact", {
        name: ecForm.name.trim(), relation: ecForm.relation.trim(), phone: ecForm.phone.trim(),
      });
      toast.success("Emergency contact updated");
      setEcOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    finally { setEcSaving(false); }
  };

  const openPreview = async (d) => {
    try {
      const token = localStorage.getItem("assetiq_token");
      const res = await fetch(`${api.defaults.baseURL}/people/employees/${emp.id}/documents/${d.id}/preview`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Preview failed");
      const blob = await res.blob();
      setPreview({ d, url: URL.createObjectURL(blob), ct: blob.type || d.content_type || "" });
    } catch (err) { toast.error(err.message || "Preview failed"); }
  };
  const closePreview = () => {
    if (preview?.url) URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  if (notLinked) {
    return (
      <div className="max-w-2xl mx-auto text-center py-16" data-testid="my-profile-not-linked">
        <User className="w-14 h-14 mx-auto mb-3 text-slate-300" />
        <div className="text-slate-500 text-sm">
          You do not have an employee profile linked to your Asset IQ account yet.<br />
          Please contact HR to have your accounts connected.
        </div>
      </div>
    );
  }
  if (!emp) return <div className="text-slate-400 text-sm">Loading your profile…</div>;

  return (
    <div className="space-y-6 max-w-5xl" data-testid="my-profile-page">
      <div>
        <div className="text-xs font-semibold tracking-widest uppercase text-[#7C1D3A]">People IQ · Self-Service</div>
        <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1 flex items-center gap-2">
          My Profile
          <Badge variant="outline" className="font-mono text-xs">{emp.employee_code}</Badge>
        </h1>
        <p className="text-slate-500 mt-1 text-sm">
          {emp.full_name} · {emp.official_email || emp.personal_email || "—"}
        </p>
      </div>

      <Card>
        <CardHeader className="flex-row items-center justify-between">
          <CardTitle className="text-base">Personal &amp; Contact</CardTitle>
          <Button className="bg-[#7C1D3A] hover:bg-[#6c1c36]" onClick={() => setDialogOpen(true)} data-testid="request-change-btn">
            <Send className="w-4 h-4 mr-2" /> Request Change
          </Button>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Kv label="Full name" v={emp.full_name} />
          <Kv label="Preferred name" v={emp.preferred_name} />
          <Kv label="Date of birth" v={formatDate(emp.date_of_birth)} />
          <Kv label="Personal email" v={emp.personal_email} />
          <Kv label="Personal mobile" v={emp.personal_mobile} />
          <Kv label="Marital status" v={emp.marital_status} />
          <Kv label="Blood group" v={emp.blood_group} />
        </CardContent>
      </Card>

      <Card data-testid="my-ec-card">
        <CardHeader className="flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2"><PhoneCall className="w-4 h-4 text-[#7C1D3A]" /> Emergency Contact</CardTitle>
          <Button variant="outline" size="sm" onClick={openEc} data-testid="my-ec-update-btn">
            {emp.emergency_contact_name ? "Update" : "Add"}
          </Button>
        </CardHeader>
        <CardContent>
          {emp.emergency_contact_name ? (
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-rose-50 text-rose-700 flex items-center justify-center"><PhoneCall className="w-4 h-4" /></div>
              <div>
                <div className="text-sm font-semibold text-slate-900" data-testid="my-ec-name">{emp.emergency_contact_name}</div>
                <div className="text-xs text-slate-500 capitalize" data-testid="my-ec-relation">{emp.emergency_contact_relation}</div>
                <div className="text-sm font-mono text-slate-900" data-testid="my-ec-phone">{emp.emergency_contact_phone}</div>
              </div>
            </div>
          ) : (
            <div className="text-sm text-slate-400" data-testid="my-ec-empty">
              No emergency contact on file. Add one — it is used only if something happens to you at work.
            </div>
          )}
          <div className="text-[11px] text-slate-400 mt-3">Changes here save instantly and are shown to HR.</div>
        </CardContent>
      </Card>

      <Card data-testid="my-salary-card">
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><IndianRupee className="w-4 h-4 text-[#7C1D3A]" /> My Salary — FY {salary?.financial_year || ""}</CardTitle></CardHeader>
        <CardContent>
          {salary?.current ? (
            <div className="space-y-3" data-testid="my-salary-values">
              <div>
                <div className="text-[10px] uppercase tracking-widest font-semibold text-[#7C1D3A] mb-1.5">Monthly</div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">Basic</div>
                    <div className="text-lg font-bold text-slate-900 mt-0.5">{salary.current.basic != null ? formatINR(Math.round(salary.current.basic / 12)) : <span className="text-sm text-slate-400 italic">pending</span>}</div>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">HRA</div>
                    <div className="text-lg font-bold text-slate-900 mt-0.5">{salary.current.hra != null ? formatINR(Math.round(salary.current.hra / 12)) : <span className="text-sm text-slate-400 italic">pending</span>}</div>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">Gross</div>
                    <div className="text-lg font-bold text-slate-900 mt-0.5">{formatINR(Math.round(salary.current.gross_salary / 12))}</div>
                  </div>
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest font-semibold text-[#7C1D3A] mb-1.5">Annually</div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="rounded-lg border border-[#f5c4d3] bg-[#7C1D3A]/[0.03] px-4 py-3">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">Basic</div>
                    <div className="text-base font-semibold text-slate-900 mt-0.5">{salary.current.basic != null ? formatINR(salary.current.basic) : <span className="text-sm text-slate-400 italic">pending</span>}</div>
                  </div>
                  <div className="rounded-lg border border-[#f5c4d3] bg-[#7C1D3A]/[0.03] px-4 py-3">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">HRA</div>
                    <div className="text-base font-semibold text-slate-900 mt-0.5">{salary.current.hra != null ? formatINR(salary.current.hra) : <span className="text-sm text-slate-400 italic">pending</span>}</div>
                  </div>
                  <div className="rounded-lg border border-[#f5c4d3] bg-[#7C1D3A]/[0.03] px-4 py-3">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">Gross</div>
                    <div className="text-base font-semibold text-[#7C1D3A] mt-0.5">{formatINR(salary.current.gross_salary)}</div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-sm text-slate-400" data-testid="my-salary-empty">No salary record for the current financial year yet.</div>
          )}
          {salary?.current && (
            <div className="text-[11px] text-slate-400 mt-3">
              Effective from {formatDate(salary.current.effective_from)}
              {salary.current.revision_note && <> · {salary.current.revision_note}</>}
            </div>
          )}
        </CardContent>
      </Card>

      <Card data-testid="my-docs-card">
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><FileText className="w-4 h-4 text-[#7C1D3A]" /> My Documents</CardTitle></CardHeader>
        <CardContent className="p-0">
          {docs.length === 0 ? (
            <div className="text-center py-8 text-slate-400 text-sm">No documents uploaded by HR yet.</div>
          ) : (
            <table className="w-full text-sm" data-testid="my-docs-table">
              <thead className="text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
                <tr>
                  <th className="text-left px-4 py-2">Title</th>
                  <th className="text-left px-4 py-2">Type</th>
                  <th className="text-left px-4 py-2">Uploaded</th>
                  <th className="text-right px-4 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {docs.map((d) => (
                  <tr key={d.id} className="border-t border-slate-100">
                    <td className="px-4 py-2 font-medium text-slate-900">
                      {d.title || d.filename}
                      {d.is_sensitive && <Badge className="ml-2 bg-rose-100 text-rose-700 text-[9px] px-1 py-0">CONFIDENTIAL</Badge>}
                    </td>
                    <td className="px-4 py-2 text-xs text-slate-600 capitalize">{(d.doc_type || "").replace(/_/g, " ")}</td>
                    <td className="px-4 py-2 text-xs text-slate-600">{formatDateTime(d.created_at)}</td>
                    <td className="px-4 py-2 text-right">
                      <Button variant="ghost" size="icon" onClick={() => openPreview(d)} title="View" data-testid={`my-doc-view-${d.id}`}>
                        <Eye className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">My Change Requests</CardTitle></CardHeader>
        <CardContent className="p-0">
          {requests.length === 0 ? (
            <div className="text-center py-8 text-slate-400 text-sm">You haven't submitted any change requests yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
                <tr>
                  <th className="text-left px-4 py-2">Submitted</th>
                  <th className="text-left px-4 py-2">Fields</th>
                  <th className="text-left px-4 py-2">Status</th>
                  <th className="text-left px-4 py-2">Note</th>
                  <th className="text-right px-4 py-2"></th>
                </tr>
              </thead>
              <tbody data-testid="my-requests-tbody">
                {requests.map((r) => {
                  const s = STATUS_META[r.status] || STATUS_META.pending;
                  return (
                    <tr key={r.id} className="border-t border-slate-100">
                      <td className="px-4 py-2 text-xs text-slate-600 whitespace-nowrap">{formatDateTime(r.created_at)}</td>
                      <td className="px-4 py-2 text-xs">{Object.keys(r.requested_changes || {}).join(", ")}</td>
                      <td className="px-4 py-2"><Badge className={s.tint}>{s.label}</Badge></td>
                      <td className="px-4 py-2 text-xs text-slate-600 italic">{r.decision_notes || r.reason || "—"}</td>
                      <td className="px-4 py-2 text-right">
                        {r.status === "pending" && (
                          <Button size="sm" variant="ghost" className="text-slate-500 hover:text-slate-700" onClick={() => withdraw(r)}>
                            Withdraw
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="request-change-dialog">
          <DialogHeader><DialogTitle>Request profile changes</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded p-2 leading-relaxed">
              Change any field below then submit. HR will review and update your master record. Legal identity fields (employee code, name, DOB, PAN, Aadhaar) can only be corrected by HR directly.
            </div>
            <div className="grid grid-cols-2 gap-3 max-h-72 overflow-auto pr-1">
              {REQUESTABLE_FIELDS.filter((f) =>
                (f.key !== "esic_number" || emp?.esic_applicable) &&
                (f.key !== "uan_number" || emp?.pf_applicable)
              ).map((f) => (
                <div key={f.key}>
                  <label className="text-[10px] uppercase font-semibold text-slate-500">{f.label}</label>
                  {f.key === "blood_group" ? (
                    <select
                      className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 text-sm bg-white"
                      value={changes[f.key] ?? ""}
                      onChange={(e) => setChanges({ ...changes, [f.key]: e.target.value })}
                      data-testid={`req-${f.key}`}
                    >
                      <option value="">—</option>
                      {BLOOD_GROUPS.map((b) => <option key={b.v} value={b.v}>{b.l}</option>)}
                    </select>
                  ) : (
                    <input
                      className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
                      value={changes[f.key] ?? ""}
                      placeholder={emp[f.key] ? `Current: ${emp[f.key]}` : "—"}
                      onChange={(e) => setChanges({ ...changes, [f.key]: e.target.value })}
                      data-testid={`req-${f.key}`}
                    />
                  )}
                </div>
              ))}
            </div>
            <div>
              <label className="text-[10px] uppercase font-semibold text-slate-500">Reason (optional)</label>
              <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="req-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={saving} className="bg-[#7C1D3A] hover:bg-[#6c1c36]" data-testid="req-submit">Submit for review</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={ecOpen} onOpenChange={setEcOpen}>
        <DialogContent className="max-w-md" data-testid="my-ec-dialog">
          <DialogHeader><DialogTitle>Emergency contact</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <label className="text-[10px] uppercase font-semibold text-slate-500">Name *</label>
              <input className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 text-sm" value={ecForm.name}
                onChange={(e) => setEcForm({ ...ecForm, name: e.target.value })} data-testid="my-ec-name-input" />
            </div>
            <div>
              <label className="text-[10px] uppercase font-semibold text-slate-500">Relationship *</label>
              <input className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 text-sm" value={ecForm.relation}
                onChange={(e) => setEcForm({ ...ecForm, relation: e.target.value })} placeholder="e.g. Spouse" data-testid="my-ec-relation-input" />
            </div>
            <div>
              <label className="text-[10px] uppercase font-semibold text-slate-500">Phone *</label>
              <input className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 text-sm" value={ecForm.phone}
                onChange={(e) => setEcForm({ ...ecForm, phone: e.target.value })} placeholder="+91…" data-testid="my-ec-phone-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEcOpen(false)}>Cancel</Button>
            <Button onClick={saveEc} disabled={ecSaving} className="bg-[#7C1D3A] hover:bg-[#6c1c36]" data-testid="my-ec-save-btn">
              {ecSaving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!preview} onOpenChange={(o) => { if (!o) closePreview(); }}>
        <DialogContent className="max-w-3xl" data-testid="my-doc-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {preview?.d?.title || preview?.d?.filename}
              {preview?.d?.is_sensitive && <Badge className="bg-rose-100 text-rose-700 text-[9px] px-1 py-0">CONFIDENTIAL</Badge>}
            </DialogTitle>
          </DialogHeader>
          {preview && (
            preview.ct.startsWith("image/") ? (
              <img src={preview.url} alt={preview.d.filename} className="max-h-[70vh] w-auto mx-auto rounded" data-testid="my-doc-preview-image" />
            ) : preview.ct === "application/pdf" ? (
              <iframe src={preview.url} title={preview.d.filename} className="w-full h-[70vh] rounded border border-slate-200" data-testid="my-doc-preview-frame" />
            ) : (
              <div className="text-sm text-slate-500 py-8 text-center" data-testid="my-doc-preview-unsupported">
                Preview is available for PDF and image files only. Please contact HR if you need a copy.
              </div>
            )
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Kv({ label, v }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{label}</div>
      <div className="text-sm text-slate-900 mt-0.5 break-words">{v || "—"}</div>
    </div>
  );
}
